import { parseThemeBlock, type ThemeBlock } from "../shared/presentation";
import { boundedKeys, exactKeys, GameSpecValidationError, glyph, integer, record, text } from "../shared/validators";

export interface GridStrategySpec {
  readonly version: 1;
  readonly kind: "grid-strategy";
  readonly title: string;
  readonly seed: number;
  readonly theme: ThemeBlock;
  readonly params: {
    readonly cols: number;
    readonly rows: number;
    /** 1 = versus the seeded rival (default); 2 = local hot-seat, mirror-fair starts. */
    readonly players?: number;
    readonly cellGlyph?: string;
  };
}

/** Tap-to-claim territory: board-capture games where the player and a rival race to control the most cells. */
export function parseGridStrategySpec(root: Record<string, unknown>): GridStrategySpec {
  exactKeys(root, ["version", "kind", "title", "seed", "theme", "params"], "game spec");
  if (root.kind !== "grid-strategy") throw new GameSpecValidationError("unsupported game kind");

  const params = record(root.params, "params");
  boundedKeys(params, ["cols", "rows"], ["players", "cellGlyph"], "params");
  const players = params.players === undefined ? undefined : integer(params.players, "params.players", 1, 2);
  const cellGlyph = glyph(params.cellGlyph, "params.cellGlyph");

  return {
    version: 1,
    kind: "grid-strategy",
    title: text(root.title, "title", 1, 80),
    seed: integer(root.seed, "seed", 0, 4_294_967_295),
    theme: parseThemeBlock(root.theme),
    params: {
      cols: integer(params.cols, "params.cols", 4, 8),
      rows: integer(params.rows, "params.rows", 4, 8),
      ...(players === undefined ? {} : { players }),
      ...(cellGlyph === undefined ? {} : { cellGlyph }),
    },
  };
}
